/**
 * ADS-aligned palette for standalone print/PDF HTML documents.
 *
 * Print HTML does not inherit the app shell theme. We always use light
 * paper + dark ink so thermal/laser drivers do not drop near-white text
 * that comes from dark UI tokens (e.g. --ads-color-text-primary: #f4f4f5).
 * Fallbacks mirror light values in src/styles/tokens/colors.css.
 */

/** @type {Readonly<Record<string, string>>} */
export const PRINT_ADS_FALLBACKS = Object.freeze({
  surface: '#ffffff',
  surfaceSunken: '#f1f5f9',
  background: '#f8fafc',
  backgroundSubtle: '#f1f5f9',
  text: '#1e293b',
  textSecondary: '#334155',
  textMuted: '#64748b',
  textInverse: '#ffffff',
  border: '#cbd5e1',
  borderSubtle: '#e2e8f0',
  borderStrong: '#94a3b8',
  primary: '#2563eb',
  primaryDark: '#1e40af',
  primarySubtle: '#eff6ff',
  secondary100: '#f3f4f6',
  secondary200: '#e5e7eb',
  secondary300: '#d1d5db'
});

/** Hard ink / paper used when a value is missing or too light to burn on thermal. */
export const PRINT_INK = '#111827';
export const PRINT_PAPER = '#ffffff';

function hexToRgbTuple(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function cssColorToRgbTuple(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim();
  if (v.startsWith('#')) return hexToRgbTuple(v);
  const m = v.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!m) return null;
  return [Math.round(Number(m[1])), Math.round(Number(m[2])), Math.round(Number(m[3]))];
}

/** Relative luminance 0–1 (sRGB, WCAG-ish). */
function relativeLuminance(rgb) {
  if (!rgb) return 0;
  const toLin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb.map(toLin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * True when a color is too light to use as thermal/laser ink (near-white dark UI).
 * @param {string} color
 */
export function isPrintInkTooLight(color) {
  const rgb = cssColorToRgbTuple(color);
  if (!rgb) return false;
  return relativeLuminance(rgb) > 0.55;
}

/**
 * Ensure text-like keys stay dark enough to print on white paper.
 * @param {Partial<typeof PRINT_ADS_FALLBACKS>} [input]
 * @returns {typeof PRINT_ADS_FALLBACKS}
 */
export function sanitizePrintAdsPalette(input) {
  const base = { ...PRINT_ADS_FALLBACKS, ...(input || {}) };
  for (const key of ['text', 'textSecondary', 'textMuted', 'borderStrong']) {
    if (isPrintInkTooLight(base[key])) {
      base[key] = PRINT_ADS_FALLBACKS[key] || PRINT_INK;
    }
  }
  // Near-black/dark surfaces → blank paper once the driver drops backgrounds.
  if (base.surface && !isPrintInkTooLight(base.surface) && relativeLuminance(cssColorToRgbTuple(base.surface)) < 0.85) {
    base.surface = PRINT_PAPER;
  }
  if (base.background && relativeLuminance(cssColorToRgbTuple(base.background)) < 0.85) {
    base.background = PRINT_ADS_FALLBACKS.background;
  }
  return base;
}

/**
 * Resolve print palette for paper output.
 * Never reads live document CSS variables (dark mode prints blank on many drivers).
 * @returns {typeof PRINT_ADS_FALLBACKS}
 */
export function resolvePrintAdsPalette() {
  return sanitizePrintAdsPalette(PRINT_ADS_FALLBACKS);
}

/**
 * CSS custom properties for standalone print documents.
 * @param {ReturnType<typeof resolvePrintAdsPalette>} [palette]
 */
export function buildPrintAdsCssVariables(palette = resolvePrintAdsPalette()) {
  const p = sanitizePrintAdsPalette(palette);
  return `
    :root {
      color-scheme: only light;
      --print-surface: ${p.surface};
      --print-surface-sunken: ${p.surfaceSunken};
      --print-background: ${p.background};
      --print-background-subtle: ${p.backgroundSubtle};
      --print-text: ${p.text};
      --print-text-secondary: ${p.textSecondary};
      --print-text-muted: ${p.textMuted};
      --print-text-inverse: ${p.textInverse};
      --print-border: ${p.border};
      --print-border-subtle: ${p.borderSubtle};
      --print-border-strong: ${p.borderStrong};
      --print-primary: ${p.primary};
      --print-primary-dark: ${p.primaryDark};
      --print-primary-subtle: ${p.primarySubtle};
      --print-secondary-100: ${p.secondary100};
      --print-secondary-200: ${p.secondary200};
      --print-secondary-300: ${p.secondary300};
    }
  `;
}

/**
 * Hard body ink rules — defense in depth when drivers ignore CSS variables / dark theme.
 * Include after variables in standalone print HTML.
 */
export function buildPrintAdsInkForceCss() {
  return `
    html, body {
      color: ${PRINT_INK} !important;
      background: ${PRINT_PAPER} !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  `;
}

/**
 * RGB tuple for jsPDF setTextColor / setDrawColor.
 * @param {'primary'|'primaryDark'|'text'|'textMuted'|'borderStrong'} key
 * @param {ReturnType<typeof resolvePrintAdsPalette>} [palette]
 * @returns {[number, number, number]}
 */
export function resolvePrintAdsRgb(key, palette = resolvePrintAdsPalette()) {
  const p = sanitizePrintAdsPalette(palette);
  const map = {
    primary: p.primary,
    primaryDark: p.primaryDark,
    text: p.text,
    textMuted: p.textMuted,
    borderStrong: p.borderStrong
  };
  return (
    cssColorToRgbTuple(map[key]) ||
    hexToRgbTuple(
      PRINT_ADS_FALLBACKS[
        key === 'borderStrong'
          ? 'borderStrong'
          : key === 'primaryDark'
            ? 'primaryDark'
            : key === 'textMuted'
              ? 'textMuted'
              : key === 'text'
                ? 'text'
                : 'primary'
      ]
    ) ||
    [0, 0, 0]
  );
}
